import React, { useEffect, useRef } from 'react';
import { getShopifyUI, getShopifySettings } from '../lib/shopifyClient';

/**
 * ShopifyBuyButton — drop-in React wrapper that uses the shared singleton
 * Shopify UI (see `lib/shopifyClient.js`). Every BUY component on the page —
 * including the inline track BUY buttons and the navbar cart toggle —
 * shares the same cart instance because they all come from one UI client.
 */

const defaultCollectionOptions = {
  product: {
    styles: {
      product: {
        '@media (min-width: 601px)': {
          'max-width': 'calc(33% - 20px)',
          'margin-left': '20px',
          'margin-bottom': '40px',
          width: 'calc(33% - 20px)',
        },
        img: { height: 'calc(100% - 15px)', position: 'absolute', left: '0', right: '0', top: '0' },
        imgWrapper: { 'padding-top': 'calc(75% + 15px)', position: 'relative', height: '0' },
        title: { color: '#FFFFFF' },
        price: { color: '#D4AF37', 'font-weight': 'bold' },
        compareAt: { color: '#A3A3A3' },
      },
      button: {
        'background-color': '#D4AF37',
        color: '#000000',
        'font-family': 'Outfit, sans-serif',
        'font-weight': '600',
        'letter-spacing': '0.05em',
        'text-transform': 'uppercase',
        ':hover': { 'background-color': '#E5C048' },
        ':focus': { 'background-color': '#E5C048' },
      },
    },
    text: { button: 'Add to cart' },
  },
  productSet: {
    styles: { products: { '@media (min-width: 601px)': { 'margin-left': '-20px' } } },
  },
  modalProduct: {
    contents: { img: false, imgWithCarousel: true, button: false, buttonWithQuantity: true },
    styles: {
      product: { '@media (min-width: 601px)': { 'max-width': '100%', 'margin-left': '0px', 'margin-bottom': '0px' } },
      button: {
        'background-color': '#D4AF37', color: '#000000',
        ':hover': { 'background-color': '#E5C048' }, ':focus': { 'background-color': '#E5C048' },
      },
    },
    text: { button: 'Add to cart' },
  },
  cart: {
    text: { total: 'Subtotal', button: 'Checkout' },
    styles: {
      button: {
        'background-color': '#D4AF37', color: '#000000',
        ':hover': { 'background-color': '#E5C048' }, ':focus': { 'background-color': '#E5C048' },
      },
    },
  },
};

const ShopifyBuyButton = ({
  shopDomain,  // kept for backward-compat — IGNORED, we always use shared client
  storefrontToken,
  componentType = 'collection',
  componentId,
  options,
  className = '',
}) => {
  const containerRef = useRef(null);
  const mountedRef = useRef(false);
  const [hasCreds, setHasCreds] = React.useState(true);

  useEffect(() => {
    let alive = true;
    getShopifySettings().then((s) => {
      if (!alive) return;
      setHasCreds(!!(s.domain && s.token));
    });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!componentId || !containerRef.current || mountedRef.current) return;
    let cancelled = false;
    getShopifyUI()
      .then((ui) => {
        if (cancelled || !containerRef.current) return;
        ui.createComponent(componentType, {
          id: componentId,
          node: containerRef.current,
          moneyFormat: '%24%7B%7Bamount%7D%7D',
          options: options || defaultCollectionOptions,
        });
        mountedRef.current = true;
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [componentType, componentId, options]);

  if (!componentId) return null;
  if (!hasCreds) {
    return (
      <div className="text-sm text-[#A3A3A3] italic" data-testid="shopify-buy-button-misconfigured">
        Shopify Buy Button is not yet configured.
      </div>
    );
  }
  return (
    <div
      ref={containerRef}
      className={className}
      data-testid={`shopify-buy-button-${componentId}`}
    />
  );
};

export default ShopifyBuyButton;
